import classNames from 'classnames';

import lang from '../../../lib/language';
import { Commission } from '../../../lib/models/crm/types';
import { formatCurrency } from '../../../lib/util/DataHelpers';

import { GlobalClassesEnum } from '../../../assets/scss/globalClasses/globalClasses.constants';
import { formatReferralDate, groupCommissionsByStatusChange } from '../../../containers/Referrals/helpers';
import { EMPTY_AMOUNT_PLACEHOLDER } from '../../../containers/Referrals/Referrals.constants';
import { useIsMobile } from '../../../hooks';
import { getCommissionStatusBadge } from '../helpers';
import ReferralsEmptyState from '../ReferralsEmptyState/ReferralsEmptyState';
import ReferralStatusBadge from '../ReferralStatusBadge/ReferralStatusBadge';

import styles from './CommissionsTabContent.module.scss';

type Props = {
  commissions: Commission[];
};

const secondaryClasses = classNames(
  GlobalClassesEnum.CommonBody2Text,
  GlobalClassesEnum.CommonTextSecondary,
);
const headerCellClasses = classNames(
  GlobalClassesEnum.CommonCaption,
  GlobalClassesEnum.CommonTextSecondary,
  styles.headerCell,
);
const cellClasses = classNames(GlobalClassesEnum.CommonBody1Text, styles.cell);

const CommissionsTabContent = ({ commissions }: Props) => {
  const { isMobile, isTablet } = useIsMobile();

  if (!commissions.length) {
    return (
      <ReferralsEmptyState
        title={lang.referralsEmptyCommissionsTitle()}
        description={lang.referralsEmptyCommissionsDescription()}
      />
    );
  }

  if (isMobile || isTablet) {
    return (
      <div className={styles.groups}>
        {groupCommissionsByStatusChange(commissions).map((group) => {
          const badge = getCommissionStatusBadge(group.status);

          return (
            <div className={styles.group} key={`${group.statusChangedAt}-${group.status}`}>
              <div className={styles.groupHeader}>
                <div className={secondaryClasses}>{formatReferralDate(group.statusChangedAt)}</div>
                <ReferralStatusBadge label={badge.label} variant={badge.variant} compact />
              </div>
              <div className={styles.groupCard}>
                {group.items.map((commission) => (
                  <div className={styles.cardRow} key={commission.id}>
                    <div className={styles.cardUser}>
                      <div className={GlobalClassesEnum.CommonBody2TextBold}>{commission.maskedEmail}</div>
                      <div className={secondaryClasses}>
                        {commission.requestId ?? EMPTY_AMOUNT_PLACEHOLDER}
                      </div>
                    </div>
                    <div className={GlobalClassesEnum.CommonBody2TextBold}>
                      {formatCurrency(commission.commission)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.table}>
      <div className={styles.headerRow}>
        <div className={headerCellClasses}>{lang.referralsColumnReferral()}</div>
        <div className={headerCellClasses}>{lang.referralsColumnRequestId()}</div>
        <div className={headerCellClasses}>{lang.referralsColumnCommission()}</div>
        <div className={headerCellClasses}>{lang.referralsColumnStatus()}</div>
        <div className={headerCellClasses}>{lang.referralsColumnStatusChange()}</div>
      </div>
      {commissions.map((commission) => {
        const badge = getCommissionStatusBadge(commission.status);

        return (
          <div className={styles.row} key={commission.id}>
            <div className={cellClasses}>{commission.maskedEmail}</div>
            <div className={cellClasses}>{commission.requestId ?? EMPTY_AMOUNT_PLACEHOLDER}</div>
            <div className={cellClasses}>{formatCurrency(commission.commission)}</div>
            <div className={cellClasses}>
              <ReferralStatusBadge label={badge.label} variant={badge.variant} />
            </div>
            <div className={cellClasses}>{formatReferralDate(commission.statusChangedAt)}</div>
          </div>
        );
      })}
    </div>
  );
};

export default CommissionsTabContent;
